import React, { useState, useEffect } from "react";
import { css } from '@emotion/css';
import { color_colors_decline, color_colors_growth, color_shades_dark, color_shades_lighter, color_text_default, color_text_light, silver_four } from "../../../constants/colors";
import { clearAssistantApiUrlOverride, getAssistantApiUrl, getDefaultAssistantApiUrl, setAssistantApiUrlOverride } from "../../../constants/Urls";
import LanguageModel from "../../../models/features/api/LanguageModel";
import ToggleIndicator from "../../../components/general/ToggleIndicator";
import Button from "../../../components/general/Button";
import HorizontalSeparator from "../../../components/structure/HorizontalSeparator";
import UserApplicationPreferencesContainer from "../../../state/containers/UserApplicationPreferencesContainer";
import { SimulatorMessage } from "./SimulatorInteraction";
import CustomScrollbars from "../../../components/structure/CustomScrollbars";
import useWindowSize from "../../../hooks/UseWindowSize";
import styled from "@emotion/styled";

const greenPowerIcon = require("../../../content/images/simulator/green-power.svg");
const removeThumbtackIcon = require("../../../content/images/simulator/remove-thumbtack.svg");
const helpIconBlack = require('../../../content/images/help-black.svg');
const fallbackIconBlack = require('../../../content/images/fallback-black.svg');
const welcomeIconBlack = require('../../../content/images/welcome-black.svg');
const exitIconBlack = require('../../../content/images/exit-black.svg');

interface SimulatorPinnedMessagesPanelProps {
    applicationId: string,
    userApplicationPreferencesContainer: UserApplicationPreferencesContainer,
    languages: LanguageModel[]
    selectedLanguage?: LanguageModel,
    requests?: SimulatorMessage[],
    handleSendMessage: (text: string) => void
    handleSendWelcome: () => void
    handleStartNewSession: () => void
    onOpenWebhookOverrides?: () => void
};

const SimulatorPinnedMessagesPanel: React.FC<SimulatorPinnedMessagesPanelProps> = (props) => {

    const windowSize = useWindowSize();
    const [isAssistantApiEditorOpen, setIsAssistantApiEditorOpen] = useState(false);
    const [assistantApiUrl, setAssistantApiUrl] = useState(getAssistantApiUrl() ?? "");
    const [activeAssistantApiUrl, setActiveAssistantApiUrl] = useState(getAssistantApiUrl() ?? "");
    const [assistantApiUrlError, setAssistantApiUrlError] = useState("");
    const [assistantApiUrlSaved, setAssistantApiUrlSaved] = useState(false);

    useEffect(() => {
        props.userApplicationPreferencesContainer.loadUserApplicationPreferences(props.applicationId);
    }, [props.applicationId])

    const toggleAssistantApiEditor = () => {
        if (!isAssistantApiEditorOpen) {
            const currentAssistantApiUrl = getAssistantApiUrl() ?? "";
            setAssistantApiUrl(currentAssistantApiUrl);
            setActiveAssistantApiUrl(currentAssistantApiUrl);
        }
        setAssistantApiUrlError("");
        setAssistantApiUrlSaved(false);
        setIsAssistantApiEditorOpen(!isAssistantApiEditorOpen);
    };

    const saveAssistantApiUrl = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const normalizedUrl = assistantApiUrl.trim().replace(/\/+$/, "");
        try {
            const parsedUrl = new URL(normalizedUrl);
            if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
                throw new Error();
            }
        }
        catch {
            setAssistantApiUrlError("Enter a valid URL beginning with http:// or https://.");
            setAssistantApiUrlSaved(false);
            return;
        }

        setAssistantApiUrlOverride(normalizedUrl);
        setAssistantApiUrl(normalizedUrl);
        setActiveAssistantApiUrl(normalizedUrl);
        setAssistantApiUrlError("");
        setAssistantApiUrlSaved(true);
    };

    const useDefaultAssistantApiUrl = () => {
        clearAssistantApiUrlOverride();
        const defaultAssistantApiUrl = getAssistantApiUrl() ?? "";
        setAssistantApiUrl(defaultAssistantApiUrl);
        setActiveAssistantApiUrl(defaultAssistantApiUrl);
        setAssistantApiUrlError("");
        setAssistantApiUrlSaved(false);
    };

    const pinnedMessages = props.userApplicationPreferencesContainer.state.currentUserApplicationPreferences?.simulatorPinnedMessages ?? [];
    const hasAssistantApiUrlChanged = assistantApiUrl !== activeAssistantApiUrl;
    const isAssistantApiUrlDifferentFromDefault = assistantApiUrl !== (getDefaultAssistantApiUrl() ?? "");
    return (
        <div className={containerStyle}>
            <PinnedMessagesHeadingWrapper>
                <div className="title-container">
                    <div className="title">Pinned Messages</div>
                </div>
                <div className={defaultButtons}>
                    <Button icon={greenPowerIcon} themes={["white-pill"]} className={greenText} text="Restart session" onClick={() => props.handleStartNewSession()} />
                    <div className={generalResponses}>
                        <Button icon={welcomeIconBlack} themes={["white-pill"]} disabled={props.requests.length > 0} className={generalResponseButton} text="Welcome" onClick={() => props.handleSendWelcome()} />
                        <Button icon={helpIconBlack} themes={["white-pill"]} className={generalResponseButton} text="Help" onClick={() => props.handleSendMessage("Help")} />
                        <Button icon={exitIconBlack} themes={["white-pill"]} className={generalResponseButton} text="Exit" onClick={() => props.handleSendMessage("Exit")} />
                        <Button icon={fallbackIconBlack} themes={["white-pill"]} className={generalResponseButton} text="System Fallback" onClick={() => props.handleSendMessage("[Unhandled Response]")} />
                        {props.onOpenWebhookOverrides && (
                            <Button themes={["white-pill"]} className={generalResponseButton} text="Webhook Overrides" onClick={() => props.onOpenWebhookOverrides()} />
                        )}
                        <Button themes={["white-pill"]} className={generalResponseButton} text={isAssistantApiEditorOpen ? "Hide Assistant API" : "Assistant API"} onClick={toggleAssistantApiEditor} />
                    </div>
                    {isAssistantApiEditorOpen && (
                        <AssistantApiEditor onSubmit={saveAssistantApiUrl}>
                            <label htmlFor="simulator-assistant-api-url">Assistant API URL</label>
                            <input
                                id="simulator-assistant-api-url"
                                type="url"
                                value={assistantApiUrl}
                                onChange={(event) => {
                                    setAssistantApiUrl(event.target.value);
                                    setAssistantApiUrlError("");
                                    setAssistantApiUrlSaved(false);
                                }}
                                placeholder="https://assistant.example.com/api"
                            />
                            {(hasAssistantApiUrlChanged || isAssistantApiUrlDifferentFromDefault) && (
                                <div className="assistant-api-actions">
                                    {hasAssistantApiUrlChanged && <Button type="submit" themes={["primary-small"]} text="Save" />}
                                    {isAssistantApiUrlDifferentFromDefault && <Button type="button" themes={["white-small"]} text="Use environment default" onClick={useDefaultAssistantApiUrl} />}
                                </div>
                            )}
                            {assistantApiUrlError && <div className="assistant-api-error">{assistantApiUrlError}</div>}
                            {assistantApiUrlSaved && <div className="assistant-api-saved">Assistant API URL updated.</div>}
                        </AssistantApiEditor>
                    )}
                </div>
            </PinnedMessagesHeadingWrapper>
            <CustomScrollbars autoHide autoHeight autoHeightMax={(windowSize.windowSize.innerHeight * .9) - 353}>
                <Phrases>
                    {pinnedMessages.map((pm, idx) =>
                        <Phrase key={idx}>
                            <PhraseChip onClick={() => props.handleSendMessage(pm.message)}>
                                {pm.message}
                            </PhraseChip>
                            <div className="remove-thumbtack" onClick={() => props.userApplicationPreferencesContainer.deleteSimulatorPinnedMessage(props.applicationId, pm.id)}>
                                <img src={removeThumbtackIcon} />
                            </div>
                        </Phrase>
                    )}
                </Phrases>
            </CustomScrollbars>
        </div>
    )
}

const PinnedMessagesHeadingWrapper = styled.div`
    padding: 0 24px;
`;

const Phrases = styled.div`
    padding: 0 24px;
`;

const greenText = css`
    color: ${color_colors_growth};
    margin-left: 0px;
`;

const generalResponseButton = css`
    margin-right: 8px;
    margin-left: 0px;
    margin-top:0px;
`;

const defaultButtons = css`
    margin-top: -15px;
`;

const generalResponses = css`
    display: flex;
    flex-wrap: wrap;
`;

const AssistantApiEditor = styled.form`
    background: ${color_shades_lighter};
    border: 1px solid ${silver_four};
    border-radius: 8px;
    margin: 8px 0 16px;
    padding: 16px;

    label {
        color: ${color_text_default};
        display: block;
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 8px;
    }

    input {
        border: 1px solid ${silver_four};
        border-radius: 6px;
        box-sizing: border-box;
        color: ${color_text_default};
        font-family: Muli;
        font-size: 14px;
        height: 40px;
        padding: 0 12px;
        width: 100%;
    }

    .assistant-api-actions {
        display: flex;
        flex-wrap: wrap;
        margin-top: 8px;

        button {
            margin: 0 8px 0 0;
        }
    }

    .assistant-api-error,
    .assistant-api-saved {
        font-size: 12px;
        margin-top: 8px;
    }

    .assistant-api-error {
        color: ${color_colors_decline};
    }

    .assistant-api-saved {
        color: ${color_colors_growth};
    }
`;

const Phrase = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    margin: 8 0;
    cursor: pointer;
    .remove-thumbtack {
        height: 56px;
        width: 24px;
        margin-left: 8px;
        img {
            width: 100%;
            margin-top: 16px;
        }
    }
    &:first-of-type {
        margin-top: 24px;
    }
    &:last-of-type {
        margin-bottom: 24px;
    }
`;

const PhraseChip = styled.div`
    background: white;
    border: 1px solid ${silver_four};
    border-radius: 8px;
    width: 100%;
    padding: 16px 24px;    
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
`;

const defaultPinned = css`
    display: flex;
    flex-direction: row;
    .defaults-expander{
        font-family: Muli;
        font-size: 14px;
        font-weight: normal;
        font-style: normal;
        padding-right: 5px;
        font-stretch: normal;
        line-height: 1.43;
        letter-spacing: normal;
        cursor: pointer;
        text-align:left;
        flex-direction: row;
        display:flex;
        padding-bottom: 15px;
        div {
            margin-right: 10px;
            margin-top: 6px;
        }
    }
`;

const fill = css`
    flex: 1;
    &.line {
        margin-left: 5px;
        border-bottom: solid 1px ${color_shades_dark};        
        margin-bottom: 24px;
    }
`;

const containerStyle = css`
    padding-top: 32px;
    max-width: 432px;
    font-family: Muli;
    font-style: normal;
    font-weight: normal;
    .title-container {
        .title {
            font-size: 18px;
            line-height: 24px;
            padding-bottom: 18px;
            color: ${color_text_default}
        }
        .subtitle {
            font-size: 14px;
            line-height: 20px;
            padding-bottom: 12px;
            color: ${color_text_light}
        }
    }
`;

export default SimulatorPinnedMessagesPanel
